// src/pages/DNSControl.tsx
import React from "react";
import {
  Tabs,
  Input,
  Space,
  Button,
  Table,
  Modal,
  Form,
  Select,
  Typography,
  Flex,
  Tooltip,
  Grid,
  Dropdown,
  Drawer,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useDataProvider, useNotification } from "@refinedev/core";
import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const RR_TYPES = ["A", "AAAA", "CNAME", "NS", "MX", "TXT", "SRV", "PTR", "CAA"];

/**
 * UI knobs
 */
const UI = {
  // Page-level min width; whole page scrolls horizontally instead of shrinking UI to micro sizes.
  PAGE_MIN_WIDTH_PX: 520,

  SEARCH_MIN_WIDTH_PX: 360,
  SEARCH_MAX_WIDTH_PX: 460,

  // polling disabled by default (recommended)
  AUTO_REFRESH_MS: 0,

  DOMAINS_PAGE_SIZE: 25,
  RECORDS_PAGE_SIZE: 50,
  ALL_RECORDS_PAGE_SIZE: 50,

  // When opening any Drawer/Modal from actions, attempt to snap to top-left.
  SCROLL_TO_TOP_ON_OPEN: true,

  // full-screen editing on phones
  DRAWER_DESKTOP_WIDTH_PX: 420,
  DRAWER_MOBILE_WIDTH: "100vw" as const,
  MODAL_DESKTOP_WIDTH_PX: 520,
};

function useQueryParams() {
  const loc = useLocation();
  return React.useMemo(() => new URLSearchParams(loc.search), [loc]);
}

function useAutoRefresh(cb: () => void, ms: number) {
  React.useEffect(() => {
    if (!ms || ms <= 0) return;
    const id = setInterval(cb, ms);
    return () => clearInterval(id);
  }, [cb, ms]);
}

// "mobile" == < sm breakpoint
function useIsMobile() {
  const screens = useBreakpoint();
  return !screens.sm;
}

function isValidDnsName(v: string): boolean {
  if (!v) return false;
  if (v.length > 253) return false;
  return /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.?$/.test(
    v,
  );
}

function filterRows<T extends Record<string, any>>(
  rows: T[] | undefined,
  search: string,
) {
  if (!rows) return [];
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    Object.values(row).some(
      (v) => v != null && String(v).toLowerCase().includes(q),
    ),
  );
}

function scrollToTopLeft() {
  if (!UI.SCROLL_TO_TOP_ON_OPEN) return;

  const el = document.scrollingElement || document.documentElement;

  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    window.scrollTo(0, 0);
  }

  try {
    el.scrollTop = 0;
    el.scrollLeft = 0;
  } catch {}

  try {
    document.body.scrollTop = 0;
    document.body.scrollLeft = 0;
  } catch {}
}

/**
 * On mobile, the page often isn't the scroll container once Modal/Drawer opens (AntD locks body).
 * This attempts to scroll AntD overlay bodies to the top as well.
 */
function scrollOverlayBodyToTop() {
  try {
    const modalBodies = document.querySelectorAll(".ant-modal-body");
    modalBodies.forEach((b) => ((b as HTMLElement).scrollTop = 0));
  } catch {}

  try {
    const drawerBodies = document.querySelectorAll(".ant-drawer-body");
    drawerBodies.forEach((b) => ((b as HTMLElement).scrollTop = 0));
  } catch {}
}

/**
 * Call when any overlay (Modal/Drawer) opens.
 * - snaps page top-left (best effort)
 * - after paint, scroll overlay body to top (reliable on Android Chrome)
 */
function onOverlayOpened() {
  scrollToTopLeft();
  setTimeout(() => {
    scrollOverlayBodyToTop();
  }, 0);
}

/**
 * Focus helper for mobile: focusing an input is often the most reliable way to bring
 * the user directly to the form (and open the keyboard properly).
 */
function focusLater(ref: React.RefObject<any>, delayMs = 50) {
  setTimeout(() => {
    try {
      ref.current?.focus?.();
    } catch {}
  }, delayMs);
}

export default function DNSControl() {
  const [activeKey, setActiveKey] = React.useState<string>("domains");
  const [search, setSearch] = React.useState("");
  const q = useQueryParams();
  const domainName = q.get("domain") || "";

  // If user selects a domain, auto-switch to Records view.
  React.useEffect(() => {
    if (domainName) setActiveKey("records");
    else if (activeKey === "records") setActiveKey("domains");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName]);

  const isRecordsDisabled = !domainName;

  return (
    <div style={{ minWidth: UI.PAGE_MIN_WIDTH_PX, padding: 16 }}>
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={12}
        style={{ marginBottom: 12 }}
      >
        <Title level={4} style={{ margin: 0, whiteSpace: "nowrap" }}>
          DNS Control
        </Title>

        <Input
          allowClear
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: 360,
            maxWidth: UI.SEARCH_MAX_WIDTH_PX,
            minWidth: UI.SEARCH_MIN_WIDTH_PX,
            flex: "0 0 auto",
          }}
          suffix={
            search ? (
              <CloseCircleOutlined
                onClick={() => setSearch("")}
                style={{ cursor: "pointer" }}
              />
            ) : undefined
          }
        />
      </Flex>

      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        items={[
          { key: "domains", label: "Domains" },
          {
            key: "records",
            label: domainName ? `Records: ${domainName}` : "Records",
            disabled: isRecordsDisabled,
          },
          { key: "all", label: "All Records" },
        ]}
      />

      {activeKey === "domains" ? (
        <DomainList search={search} />
      ) : activeKey === "records" ? (
        <DomainRecordsPanel search={search} />
      ) : (
        <AllRecordsPane search={search} />
      )}
    </div>
  );
}

// named export for App.tsx import convenience
export { DNSControl };

/* -------------------- Domains pane -------------------- */
function DomainList({ search }: { search: string }) {
  const isMobile = useIsMobile();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const q = useQueryParams();
  const navigate = useNavigate();

  const [domains, setDomains] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const selectedName = q.get("domain") || "";

  // Drawer for domain view/edit
  const [domainDrawerOpen, setDomainDrawerOpen] = React.useState(false);
  const [domainDrawerMode, setDomainDrawerMode] = React.useState<
    "view" | "edit"
  >("edit");
  const [activeDomain, setActiveDomain] = React.useState<any | null>(null);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(UI.DOMAINS_PAGE_SIZE);

  const load = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    setLoading(true);
    try {
      const res = await dataProvider.getList({
        resource: "domains",
        pagination: { current: 1, pageSize: 500 },
        sorters: [{ field: "name", order: "asc" }],
      });
      setDomains(res.data);
      setPage(1);
    } catch (err: any) {
      open?.({
        type: "error",
        message: err?.message || "Failed to load domains",
        description: err?.body?.details || err?.body?.hint,
      });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, open]);

  React.useEffect(() => {
    load();
  }, [load]);

  // useAutoRefresh(load, UI.AUTO_REFRESH_MS);
  usePGBroadcastRefresh({
    topics: ["dns.domains"],
    onChange: load,
  });

  const filtered = React.useMemo(
    () => filterRows(domains, search),
    [domains, search],
  );

  const total = filtered.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, maxPage);
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const selectDomain = (name: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set("domain", name);
    params.delete("record");
    navigate({ search: params.toString() });
  };

  const openDomainDrawer = (dom: any, mode: "view" | "edit") => {
    onOverlayOpened();
    setActiveDomain(dom);
    setDomainDrawerMode(mode);
    setDomainDrawerOpen(true);
  };

  const doDeleteDomain = (dom: any) => {
    onOverlayOpened();
    Modal.confirm({
      title: "Delete domain?",
      content: `Are you sure you want to delete ${dom?.name}?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dataProvider.delete({ resource: "domains", id: dom.id });
          open?.({ type: "success", message: "Domain deleted" });
          const params = new URLSearchParams(window.location.search);
          if (params.get("domain") === dom.name) {
            params.delete("domain");
            params.delete("record");
            navigate({ search: params.toString() });
          }
          load();
        } catch (err: any) {
          open?.({
            type: "error",
            message: err?.message || "Failed to delete domain",
            description: err?.body?.details || err?.body?.hint,
          });
        }
      },
    });
  };

  const columns = [
    {
      key: "actions",
      title: "",
      width: 40,
      render: (_: any, record: any) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "select",
                icon: <CheckOutlined />,
                label: "Select",
                onClick: () => selectDomain(record.name),
              },
              {
                key: "view",
                icon: <EyeOutlined />,
                label: "View",
                onClick: () => openDomainDrawer(record, "view"),
              },
              {
                key: "edit",
                icon: <EditOutlined />,
                label: "Edit",
                onClick: () => openDomainDrawer(record, "edit"),
              },
              {
                key: "delete",
                icon: <DeleteOutlined style={{ color: "red" }} />,
                label: <span style={{ color: "red" }}>Delete</span>,
                onClick: () => doDeleteDomain(record),
              },
            ],
          }}
        >
          <Button size="small" type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 320,
      render: (value: string, record: any) => (
        <Button
          type={value === selectedName ? "primary" : "link"}
          onClick={() => selectDomain(record.name)}
          style={{ padding: 0 }}
        >
          {value}
        </Button>
      ),
    },
    { title: "Type", dataIndex: "type", width: 120 },
  ];

  return (
    <div>
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={8}
        style={{ marginBottom: 8 }}
      >
        <Text strong style={{ whiteSpace: "nowrap" }}>
          Domains
        </Text>
        <Space>
          <Tooltip title="Reload">
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={load}
              loading={loading}
            />
          </Tooltip>
          <CreateDomainButton onCreated={load} />
        </Space>
      </Flex>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={paged}
        pagination={{
          current: pageSafe,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        scroll={undefined}
      />

      <DomainDrawer
        open={domainDrawerOpen}
        mode={domainDrawerMode}
        domain={activeDomain}
        isMobile={isMobile}
        onClose={() => {
          setDomainDrawerOpen(false);
          setActiveDomain(null);
        }}
        onSaved={() => {
          setDomainDrawerOpen(false);
          setActiveDomain(null);
          load();
        }}
      />
    </div>
  );
}

function CreateDomainButton({ onCreated }: { onCreated?: () => void }) {
  const isMobile = useIsMobile();
  const firstInputRef = React.useRef<any>(null);

  const [openModal, setOpenModal] = React.useState(false);
  const [form] = Form.useForm();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!isValidDnsName(values.name)) {
      open?.({ type: "warning", message: "Invalid domain name" });
      return;
    }
    try {
      await dataProvider.create({
        resource: "domains",
        variables: { name: values.name, type: values.type || "NATIVE" },
      });
      open?.({ type: "success", message: "Domain created" });
      setOpenModal(false);
      form.resetFields();
      onCreated?.();
    } catch (err: any) {
      open?.({
        type: "error",
        message: err?.message || "Failed to create domain",
        description: err?.body?.details || err?.body?.hint,
      });
    }
  };

  return (
    <>
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() => {
          onOverlayOpened();
          setOpenModal(true);
        }}
      />
      <Modal
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={handleOk}
        title="New Domain"
        okText="Save"
        destroyOnClose
        afterOpenChange={(opened) => {
          if (!opened) return;
          onOverlayOpened();
          focusLater(firstInputRef, 80);
        }}
        width={isMobile ? "100vw" : UI.MODAL_DESKTOP_WIDTH_PX}
        centered={!isMobile}
        style={
          isMobile
            ? { top: 0, margin: 0, paddingBottom: 0, maxWidth: "100vw" }
            : undefined
        }
        bodyStyle={
          isMobile
            ? {
                height: "calc(100vh - 110px)",
                overflow: "auto",
              }
            : undefined
        }
      >
        <Form form={form} layout="vertical" initialValues={{ type: "NATIVE" }}>
          <Form.Item
            name="name"
            label="Domain name"
            rules={[{ required: true }]}
          >
            <Input ref={firstInputRef} autoFocus />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/* -------------------- Domain drawer -------------------- */
function DomainDrawer({
  open,
  mode,
  domain,
  isMobile,
  onClose,
  onSaved,
}: {
  open: boolean;
  mode: "view" | "edit";
  domain: any | null;
  isMobile: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const firstInputRef = React.useRef<any>(null);

  const [form] = Form.useForm();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open: notify } = useNotification();
  const readOnly = mode === "view";

  React.useEffect(() => {
    if (!domain) return;
    form.setFieldsValue({ name: domain.name, type: domain.type ?? "NATIVE" });
  }, [domain, form]);

  React.useEffect(() => {
    if (!open) return;
    onOverlayOpened();
    // Focus even in "view" mode (mobile-friendly)
    focusLater(firstInputRef, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    if (!domain) return;
    const values = await form.validateFields();
    if (!isValidDnsName(values.name)) {
      notify?.({ type: "warning", message: "Invalid domain name" });
      return;
    }
    try {
      await dataProvider.update({
        resource: "domains",
        id: domain.id,
        variables: { name: values.name, type: values.type ?? "NATIVE" },
      });
      notify?.({ type: "success", message: "Domain updated" });
      onSaved();
    } catch (err: any) {
      notify?.({
        type: "error",
        message: err?.message || "Failed to update domain",
        description: err?.body?.details || err?.body?.hint,
      });
    }
  };

  const drawerWidth = isMobile
    ? UI.DRAWER_MOBILE_WIDTH
    : UI.DRAWER_DESKTOP_WIDTH_PX;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={drawerWidth as any}
      destroyOnClose
      style={isMobile ? { maxWidth: "100vw" } : undefined}
      afterOpenChange={(opened) => {
        if (!opened) return;
        onOverlayOpened();
        // Focus even in "view" mode
        focusLater(firstInputRef, 80);
      }}
      title={
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text
            strong
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {mode === "view" ? "View domain" : "Edit domain"}
          </Text>
          {domain ? (
            <Text
              type="secondary"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              #{domain.id} • {domain.name}
            </Text>
          ) : null}
        </div>
      }
      extra={
        mode === "edit" ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSave} disabled={!domain}>
              Save
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {!domain ? (
        <Text type="secondary">No domain selected.</Text>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input ref={firstInputRef} autoFocus readOnly={readOnly} />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Input readOnly />
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}

/* -------------------- Domain records panel -------------------- */
function DomainRecordsPanel({ search }: { search: string }) {
  const isMobile = useIsMobile();
  const q = useQueryParams();
  const domainName = q.get("domain");
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();
  const navigate = useNavigate();

  const [domain, setDomain] = React.useState<any | null>(null);
  const [records, setRecords] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState<"view" | "edit">("edit");
  const [activeRecord, setActiveRecord] = React.useState<any | null>(null);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(UI.RECORDS_PAGE_SIZE);

  const suppressAutoOpenRef = React.useRef(false);

  const loadDomain = React.useCallback(
    async (name: string) => {
      const res = await dataProvider.getList({
        resource: "domains",
        pagination: { current: 1, pageSize: 1 },
        sorters: [{ field: "name", order: "asc" }],
        filters: [{ field: "name", operator: "eq", value: name }],
      });
      return res.data?.[0] ?? null;
    },
    [dataProvider],
  );

  const loadRecords = React.useCallback(
    async (domain_id: number) => {
      const res = await dataProvider.getList({
        resource: "records",
        pagination: { current: 1, pageSize: 10000 },
        sorters: [{ field: "name", order: "asc" }],
        filters: [{ field: "domain_id", operator: "eq", value: domain_id }],
      });
      return res.data;
    },
    [dataProvider],
  );
  const refreshAll = React.useCallback(async () => {
    if (!domainName) {
      setDomain(null);
      setRecords([]);
      return;
    }
    setLoading(true);
    try {
      const dom = await loadDomain(domainName);
      setDomain(dom);
      if (dom) {
        const recs = await loadRecords(dom.id);
        setRecords(recs);
        setPage(1);
      } else {
        setRecords([]);
      }
    } catch (err: any) {
      open?.({ type: "error", message: "Failed to load records" });
    } finally {
      setLoading(false);
    }
  }, [domainName, loadDomain, loadRecords, open]);

  React.useEffect(() => {
    refreshAll();
  }, [refreshAll]);
  // useAutoRefresh(refreshAll, UI.AUTO_REFRESH_MS);
  usePGBroadcastRefresh({
    topics: ["dns.domains", "dns.records"],
    onChange: refreshAll,
  });

  // auto-open via ?record=
  React.useEffect(() => {
    const recId = q.get("record");
    if (!recId) return;
    if (!records.length) return;
    if (drawerOpen) return;
    if (suppressAutoOpenRef.current) {
      suppressAutoOpenRef.current = false;
      return;
    }
    const found = records.find((r) => String(r.id) === recId);
    if (found) {
      onOverlayOpened();
      setActiveRecord(found);
      setDrawerMode("edit");
      setDrawerOpen(true);
    }
  }, [q, records, drawerOpen]);

  if (!domainName) return <Text type="secondary">Select a domain.</Text>;

  const filtered = filterRows(records, search);
  const total = filtered.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, maxPage);
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const setRecordParam = (recordId?: string) => {
    const params = new URLSearchParams(window.location.search);
    if (domain?.name) params.set("domain", domain.name);
    if (recordId) params.set("record", recordId);
    else params.delete("record");
    navigate({ search: params.toString() });
  };

  const openDrawerFor = (record: any, mode: "view" | "edit") => {
    onOverlayOpened();
    setRecordParam(String(record.id));
    setActiveRecord(record);
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const doDelete = (record: any) => {
    onOverlayOpened();
    Modal.confirm({
      title: "Delete record?",
      content: `Delete ${record?.name} (${record?.type})?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dataProvider.delete({ resource: "records", id: record.id });
          open?.({ type: "success", message: "Record deleted" });
          if (activeRecord?.id === record.id) {
            suppressAutoOpenRef.current = true;
            setDrawerOpen(false);
            setActiveRecord(null);
            setRecordParam(undefined);
          }
          refreshAll();
        } catch (err: any) {
          open?.({
            type: "error",
            message: err?.message || "Failed to delete record",
            description: err?.body?.details || err?.body?.hint,
          });
        }
      },
    });
  };

  const columns = [
    {
      key: "actions",
      title: "",
      width: 40,
      render: (_: any, record: any) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "view",
                icon: <EyeOutlined />,
                label: "View",
                onClick: () => openDrawerFor(record, "view"),
              },
              {
                key: "edit",
                icon: <EditOutlined />,
                label: "Edit",
                onClick: () => openDrawerFor(record, "edit"),
              },
              {
                key: "delete",
                icon: <DeleteOutlined style={{ color: "red" }} />,
                label: <span style={{ color: "red" }}>Delete</span>,
                onClick: () => doDelete(record),
              },
            ],
          }}
        >
          <Button size="small" type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 220,
      render: (_: any, record: any) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => openDrawerFor(record, "view")}
        >
          {record.name}
        </Button>
      ),
    },
    { title: "Type", dataIndex: "type", width: 90 },
    { title: "Content", dataIndex: "content", width: 240 },
    { title: "TTL", dataIndex: "ttl", width: 90 },
  ];

  return (
    <div>
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={8}
        style={{ marginBottom: 8 }}
      >
        <Text strong>Records for {domain ? domain.name : domainName}</Text>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={refreshAll}
            loading={loading}
          />
          {domain && (
            <CreateRecordButton domain={domain} onCreated={refreshAll} />
          )}
        </Space>
      </Flex>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={paged}
        pagination={{
          current: pageSafe,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100", "200"],
        }}
        scroll={undefined}
      />

      <RecordDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={activeRecord}
        isMobile={isMobile}
        onClose={() => {
          suppressAutoOpenRef.current = true;
          setDrawerOpen(false);
          setActiveRecord(null);
          setRecordParam(undefined);
        }}
        onSaved={() => {
          suppressAutoOpenRef.current = true;
          setDrawerOpen(false);
          setActiveRecord(null);
          setRecordParam(undefined);
          refreshAll();
        }}
      />
    </div>
  );
}

function CreateRecordButton({
  domain,
  onCreated,
}: {
  domain: any;
  onCreated?: () => void;
}) {
  const isMobile = useIsMobile();
  const firstInputRef = React.useRef<any>(null);

  const [openModal, setOpenModal] = React.useState(false);
  const [form] = Form.useForm();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open } = useNotification();

  React.useEffect(() => {
    form.setFieldsValue({ name: domain.name });
  }, [domain, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!isValidDnsName(values.name)) {
      open?.({ type: "warning", message: "Invalid record name" });
      return;
    }
    try {
      await dataProvider.create({
        resource: "records",
        variables: {
          domain_id: domain.id,
          name: values.name,
          type: values.type,
          content: values.content,
          ttl: values.ttl ? Number(values.ttl) : null,
          prio:
            values.type === "MX" && values.prio ? Number(values.prio) : null,
        },
      });
      open?.({ type: "success", message: "Record created" });
      setOpenModal(false);
      form.resetFields();
      form.setFieldsValue({ name: domain.name, type: "A", ttl: 3600 });
      onCreated?.();
    } catch (err: any) {
      open?.({
        type: "error",
        message: err?.message || "Failed to create record",
        description: err?.body?.details || err?.body?.hint,
      });
    }
  };

  return (
    <>
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() => {
          onOverlayOpened();
          setOpenModal(true);
        }}
      >
        Add
      </Button>
      <Modal
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={handleOk}
        title={`New record for ${domain.name}`}
        destroyOnClose
        afterOpenChange={(opened) => {
          if (!opened) return;
          onOverlayOpened();
          focusLater(firstInputRef, 80);
        }}
        width={isMobile ? "100vw" : UI.MODAL_DESKTOP_WIDTH_PX}
        centered={!isMobile}
        style={
          isMobile
            ? { top: 0, margin: 0, paddingBottom: 0, maxWidth: "100vw" }
            : undefined
        }
        bodyStyle={
          isMobile
            ? {
                height: "calc(100vh - 110px)",
                overflow: "auto",
              }
            : undefined
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: domain.name, type: "A", ttl: 3600 }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input ref={firstInputRef} autoFocus />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select>
              {RR_TYPES.map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="ttl" label="TTL">
            <Input />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("type") === "MX" ? (
                <Form.Item name="prio" label="Priority">
                  <Input />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/* -------------------- Record drawer (shared) -------------------- */
function RecordDrawer({
  open,
  mode,
  record,
  isMobile,
  onClose,
  onSaved,
}: {
  open: boolean;
  mode: "view" | "edit";
  record: any | null;
  isMobile: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const firstInputRef = React.useRef<any>(null);

  const [form] = Form.useForm();
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();
  const { open: notify } = useNotification();
  const readOnly = mode === "view";

  React.useEffect(() => {
    if (!record) return;
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      content: record.content,
      ttl: record.ttl ?? "",
      prio: record.prio ?? "",
    });
  }, [record, form]);

  React.useEffect(() => {
    if (!open) return;
    onOverlayOpened();
    // ✅ Focus even in "view" mode (mobile-friendly)
    focusLater(firstInputRef, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    if (!record) return;
    const values = await form.validateFields();
    if (!isValidDnsName(values.name)) {
      notify?.({ type: "warning", message: "Invalid record name" });
      return;
    }
    if (!RR_TYPES.includes(values.type)) {
      notify?.({ type: "warning", message: "Unsupported RR type" });
      return;
    }
    try {
      await dataProvider.update({
        resource: "records",
        id: record.id,
        variables: {
          name: values.name,
          type: values.type,
          content: values.content,
          ttl: values.ttl ? Number(values.ttl) : null,
          prio:
            values.type === "MX" && values.prio ? Number(values.prio) : null,
        },
      });
      notify?.({ type: "success", message: "Record updated" });
      onSaved();
    } catch (err: any) {
      notify?.({
        type: "error",
        message: err?.message || "Failed to update record",
        description: err?.body?.details || err?.body?.hint,
      });
    }
  };

  const drawerWidth = isMobile
    ? UI.DRAWER_MOBILE_WIDTH
    : UI.DRAWER_DESKTOP_WIDTH_PX;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={drawerWidth as any}
      destroyOnClose
      style={isMobile ? { maxWidth: "100vw" } : undefined}
      afterOpenChange={(opened) => {
        if (!opened) return;
        onOverlayOpened();
        // ✅ Focus even in "view" mode
        focusLater(firstInputRef, 80);
      }}
      title={
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Text
            strong
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {mode === "view" ? "View DNS record" : "Edit DNS record"}
          </Text>
          {record ? (
            <Text
              type="secondary"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              #{record.id} • {record.name}
            </Text>
          ) : null}
        </div>
      }
      extra={
        mode === "edit" ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSave} disabled={!record}>
              Save
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {!record ? (
        <Text type="secondary">No record selected.</Text>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input ref={firstInputRef} autoFocus readOnly={readOnly} />
          </Form.Item>

          {/* In view mode, render Type as a read-only Input so it can still focus/scroll nicely on mobile */}
          {readOnly ? (
            <Form.Item name="type" label="Type">
              <Input readOnly />
            </Form.Item>
          ) : (
            <Form.Item name="type" label="Type" rules={[{ required: true }]}>
              <Select>
                {RR_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>
                    {t}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true }]}
          >
            <Input readOnly={readOnly} />
          </Form.Item>

          <Form.Item name="ttl" label="TTL">
            <Input readOnly={readOnly} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("type") === "MX" ? (
                <Form.Item name="prio" label="Priority">
                  <Input readOnly={readOnly} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}

/* -------------------- All records pane -------------------- */
/* -------------------- All records pane -------------------- */
function AllRecordsPane({ search }: { search: string }) {
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider()();
  const { open: notify } = useNotification();

  const [records, setRecords] = React.useState<any[]>([]);
  const [domainsById, setDomainsById] = React.useState<Record<number, string>>(
    {},
  );
  const [loading, setLoading] = React.useState(false);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(UI.ALL_RECORDS_PAGE_SIZE);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerMode, setDrawerMode] = React.useState<"view" | "edit">("edit");
  const [activeRecord, setActiveRecord] = React.useState<any | null>(null);

  const errToast = React.useCallback(
    (err: any, fallback: string) => {
      notify?.({
        type: "error",
        message: err?.message || fallback,
        description: err?.body?.details || err?.body?.hint,
      });
    },
    [notify],
  );

  const loadRecords = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const res = await dataProvider.getList({
      resource: "records",
      pagination: { current: 1, pageSize: 5000 },
      sorters: [{ field: "name", order: "asc" }],
    });
    setRecords(res.data ?? []);
    setPage(1);
  }, [dataProvider]);

  const loadDomains = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    const res = await dataProvider.getList({
      resource: "domains",
      pagination: { current: 1, pageSize: 5000 },
      sorters: [{ field: "name", order: "asc" }],
    });
    setDomainsById(() => {
      const map: Record<number, string> = {};
      (res.data ?? []).forEach((d: any) => (map[d.id] = d.name));
      return map;
    });
  }, [dataProvider]);

  const loadAll = React.useCallback(async () => {
    if (!dataProvider?.getList) return;
    setLoading(true);
    try {
      await Promise.all([loadRecords(), loadDomains()]);
    } catch (err: any) {
      errToast(err, "Failed to load records/domains");
    } finally {
      setLoading(false);
    }
  }, [dataProvider, loadDomains, loadRecords, errToast]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Refresh only what's needed
  usePGBroadcastRefresh({
    topics: ["dns.records"],
    onChange: () => {
      loadRecords().catch((err) => errToast(err, "Failed to refresh records"));
    },
  });

  usePGBroadcastRefresh({
    topics: ["dns.domains"],
    onChange: () => {
      loadDomains().catch((err) => errToast(err, "Failed to refresh domains"));
    },
  });

  const filtered = React.useMemo(
    () => filterRows(records, search),
    [records, search],
  );

  const total = filtered.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, maxPage);
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const openDrawerFor = (record: any, mode: "view" | "edit") => {
    onOverlayOpened();
    setActiveRecord(record);
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const doDelete = (record: any) => {
    onOverlayOpened();
    Modal.confirm({
      title: "Delete record?",
      content: `Delete ${record?.name} (${record?.type})?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dataProvider.delete({ resource: "records", id: record.id });
          notify?.({ type: "success", message: "Record deleted" });
          if (activeRecord?.id === record.id) {
            setDrawerOpen(false);
            setActiveRecord(null);
          }
          await loadRecords();
        } catch (err: any) {
          errToast(err, "Failed to delete record");
        }
      },
    });
  };

  const columns = [
    {
      key: "actions",
      title: "",
      width: 40,
      render: (_: any, record: any) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "view",
                icon: <EyeOutlined />,
                label: "View",
                onClick: () => openDrawerFor(record, "view"),
              },
              {
                key: "edit",
                icon: <EditOutlined />,
                label: "Edit",
                onClick: () => openDrawerFor(record, "edit"),
              },
              {
                key: "delete",
                icon: <DeleteOutlined style={{ color: "red" }} />,
                label: <span style={{ color: "red" }}>Delete</span>,
                onClick: () => doDelete(record),
              },
            ],
          }}
        >
          <Button size="small" type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 220,
      render: (_: any, record: any) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => openDrawerFor(record, "view")}
        >
          {record.name}
        </Button>
      ),
    },
    { title: "Type", dataIndex: "type", width: 90 },
    { title: "Content", dataIndex: "content", width: 260 },
    { title: "TTL", dataIndex: "ttl", width: 90 },
    {
      title: "Domain",
      dataIndex: "domain_id",
      width: 220,
      render: (v: number) => domainsById[v] ?? v,
    },
  ];

  return (
    <div>
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={8}
        style={{ marginBottom: 8 }}
      >
        <Text strong>All DNS records</Text>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={loadAll}
          loading={loading}
        />
      </Flex>

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={paged}
        pagination={{
          current: pageSafe,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100", "200"],
        }}
      />

      <RecordDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={activeRecord}
        isMobile={isMobile}
        onClose={() => {
          setDrawerOpen(false);
          setActiveRecord(null);
        }}
        onSaved={() => {
          setDrawerOpen(false);
          setActiveRecord(null);
          loadRecords().catch(() => {});
        }}
      />
    </div>
  );
}
